/**
 * The audit log of money and rewards: every store purchase step (redeem, credit, consume, store notification, refund /
 * chargeback, refusal) and every rewarded ad step (ticket, verification callback, grant, expiry) becomes
 *  - one document of the collection `monetization_audit` (id = zero-padded time + sequence + random tail, so ids sort
 *    by time), written in the same storage batch as the change it records where there is one (a credit and its entry
 *    are stored together or not at all), kept by count and age (config.monetizationAudit, checked every 500 entries
 *    and at most every 10 minutes: the oldest go first);
 *  - one log line 'monetization' with the same facts.
 * An entry names the player by id only, never holds a purchase token (the purchaseId is a hash of it), an order id's
 * payment details, a signature or a credential. It outlives an account deletion like the purchases themselves
 * (financial records: refunds and chargebacks of a deleted account's purchases are still recorded).
 */
import type { JsonObject } from '../../../shared/json';
import type { PlayerId } from '../../../shared/protocol/model';
import { randomId } from '../../../shared/random';
import { type StorageBatchOp, deleteOp, putOp } from '../storage';
import { C, type ServiceDeps } from './deps';

export type MonetizationArea = 'store' | 'ads' | 'playGames';

export interface MonetizationAuditEntry {
  auditId: string;
  at: number;
  area: MonetizationArea;
  /** What happened: 'redeem', 'credit', 'refused', 'consume', 'notification', 'void', 'ticket', 'verify', 'grant', ... */
  event: string;
  playerId: PlayerId | null;
  provider: string | null;
  ok: boolean;
  /** The facts: productId, purchaseId, orderId, gold, diam, placement, ticketId, transactionId, reason, ... */
  details: JsonObject;
}

export type NewAuditEntry = Omit<MonetizationAuditEntry, 'auditId' | 'at'> & { at?: number };

const PRUNE_EVERY = 500;
const PRUNE_MIN_INTERVAL_MS = 10 * 60_000;

export class MonetizationAudit {
  private seq = 0;
  private sincePrune = 0;
  private lastPruneAt = 0;
  private pruning: Promise<void> | null = null;

  /** A signal named in every store credit entry (services/integrity.ts: the player's latest Play Integrity verdict). */
  private signal: ((playerId: PlayerId) => string) | null = null;

  constructor(private readonly d: ServiceDeps) {}

  /** Adds `integrity: signal(playerId)` to the details of every store credit entry from now on. */
  setSignal(signal: (playerId: PlayerId) => string): void {
    this.signal = signal;
  }

  /** The entry and its storage write, for a caller that stores it in one batch with the change it records. */
  prepare(e: NewAuditEntry): { entry: MonetizationAuditEntry; op: StorageBatchOp } {
    const at = e.at ?? this.d.clock.now();
    const auditId = `${String(Math.max(0, Math.floor(at))).padStart(15, '0')}-${String(this.seq++ % 1_000_000).padStart(6, '0')}-${randomId(this.d.rng, '', 5)}`;
    const details = this.signal && e.area === 'store' && e.event === 'credit' && e.playerId ? { ...e.details, integrity: this.signal(e.playerId) } : e.details;
    const entry: MonetizationAuditEntry = { auditId, at, area: e.area, event: e.event, playerId: e.playerId, provider: e.provider, ok: e.ok, details };
    return { entry, op: putOp(C.monetizationAudit, auditId, entry) };
  }

  /** Logs an entry prepared for a batch once the batch is stored. */
  stored(entry: MonetizationAuditEntry): void {
    const fields = { area: entry.area, event: entry.event, playerId: entry.playerId, provider: entry.provider, ok: entry.ok, ...entry.details };
    if (entry.ok) this.d.logger.info('monetization', fields);
    else this.d.logger.warn('monetization', fields);
    this.maybePrune();
  }

  /** Records an entry on its own (a step that changes nothing else). A storage failure is logged, never thrown. */
  async record(e: NewAuditEntry): Promise<MonetizationAuditEntry> {
    const { entry, op } = this.prepare(e);
    try {
      await this.d.storage.batch([op]);
    } catch (err) {
      this.d.logger.error('monetization audit entry not stored', { event: entry.event, err });
    }
    this.stored(entry);
    return entry;
  }

  /** Entries, newest first (optionally of one player / area), at most `limit`. */
  async list(opts: { playerId?: PlayerId; area?: MonetizationArea; limit?: number } = {}): Promise<MonetizationAuditEntry[]> {
    const all = await this.d.storage.list<MonetizationAuditEntry>(C.monetizationAudit);
    return all
      .filter((e) => (opts.playerId === undefined || e.playerId === opts.playerId) && (opts.area === undefined || e.area === opts.area))
      .sort((a, b) => (a.auditId < b.auditId ? 1 : a.auditId > b.auditId ? -1 : 0))
      .slice(0, Math.max(1, Math.min(opts.limit ?? 100, 10_000)));
  }

  private maybePrune(): void {
    if (++this.sincePrune < PRUNE_EVERY || this.pruning) return;
    const now = this.d.clock.now();
    if (now - this.lastPruneAt < PRUNE_MIN_INTERVAL_MS) return;
    this.sincePrune = 0;
    this.lastPruneAt = now;
    this.pruning = this.prune(now)
      .then(() => undefined, (err) => this.d.logger.error('monetization audit prune failed', { err }))
      .finally(() => {
        this.pruning = null;
      });
  }

  /** Deletes the entries beyond the count and age limits (oldest first). Returns how many went. */
  async prune(now = this.d.clock.now()): Promise<number> {
    const { maxEntries, maxAgeMs } = this.d.config.monetizationAudit;
    const all = (await this.d.storage.list<MonetizationAuditEntry>(C.monetizationAudit)).sort((a, b) => (a.auditId < b.auditId ? -1 : a.auditId > b.auditId ? 1 : 0));
    const drop = new Set<string>();
    for (const e of all) if (now - e.at > maxAgeMs) drop.add(e.auditId);
    const left = all.filter((e) => !drop.has(e.auditId));
    for (let i = 0; i < left.length - maxEntries; i++) drop.add(left[i].auditId);
    const ids = [...drop];
    for (let i = 0; i < ids.length; i += 500) await this.d.storage.batch(ids.slice(i, i + 500).map((id) => deleteOp(C.monetizationAudit, id)));
    return ids.length;
  }
}
